import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Pressable, type TextStyle, type ViewStyle } from 'react-native'

import { Grabber } from '../components/Grabber'
import { useScreenSize } from '../hooks/useScreenSize'
import { CopyAddressIcon } from '../icons/CopyAddressIcon'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'

/**
 * The deposit QR-code screen (Figma: Fun Mobile / qr-code-modal, node
 * 11636:2663). Presented as a nested bottom sheet: a centered "QR Code"
 * title (no close button), the framed QR with the selected network's chain
 * logos overlaid in its center, the full deposit address with a
 * "Copy address" button, and a "Done" primary button that dismisses the sheet.
 *
 * Vertical padding adapts to screen height via {@link useScreenSize} so the
 * sheet doesn't overflow on compact devices (iPhone SE).
 *
 * The scannable QR itself is host-supplied (`qrCode`, e.g.
 * `react-native-qrcode-svg`) so the package stays `react-native-svg`-free;
 * this screen renders the themed frame and the centered chain-logo overlay.
 */
export interface QrCodeScreenProps {
  /** The rendered QR code node (host-supplied). */
  qrCode: ReactNode
  /**
   * Chain logos centered on the QR — the selected network's icons (see
   * {@link QrChainCluster}). Omit to render the QR with no center overlay.
   */
  centerIcon?: ReactNode
  /** The full deposit address shown (wrapping) below the QR. */
  address?: string
  /** How long the "Copied" confirmation stays on the Copy button (ms). */
  copiedFeedbackMs?: number
  onClose?: () => void
  /**
   * Copy the full address to the clipboard. Omit to hide the Copy button.
   * May return whether the copy succeeded; when it resolves `false` the
   * "Copied!" confirmation is suppressed (a `void` return is treated as
   * success).
   */
  onCopyAddress?: () => void | Promise<boolean>
}

export function QrCodeScreen(props: QrCodeScreenProps) {
  const { t } = useFunkitTranslation()
  const {
    qrCode,
    centerIcon,
    address,
    copiedFeedbackMs = 1800,
    onClose,
    onCopyAddress,
  } = props
  const doneLabel = t('common.done')
  const { isShort } = useScreenSize()
  const theme = useTheme()

  const [copied, setCopied] = useState(false)
  const copyTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(copyTimer.current), [])

  const handleCopy = async () => {
    // Only confirm "Copied!" when the write actually landed: copyToClipboard
    // returns false on its warn-only no-op path (e.g. no clipboard module), so
    // we don't claim success falsely. A void-returning handler is success.
    const ok = await onCopyAddress?.()
    if (ok === false) {
      return
    }
    setCopied(true)
    clearTimeout(copyTimer.current)
    copyTimer.current = setTimeout(() => setCopied(false), copiedFeedbackMs)
  }

  return (
    <Box
      borderTopLeftRadius="modal"
      borderTopRightRadius="modal"
      style={[sheet, { backgroundColor: theme.background.modal }]}
    >
      {/* Top bar: grabber · centered title (no back/close in this variant) */}
      <Box alignItems="center" style={topBar}>
        <Grabber />
        <Text
          variant="bodyMedium"
          style={{ color: theme.content.elements.primary }}
        >
          {t('qrCode.title')}
        </Text>
      </Box>
      <Box style={{ paddingVertical: isShort ? 0 : 16 }}>
        <Box
          alignItems="center"
          justifyContent="center"
          style={[content, { paddingVertical: isShort ? 24 : 82 }]}
        >
          <Box
            borderWidth={1.5}
            alignItems="center"
            justifyContent="center"
            style={[
              qrFrame,
              {
                backgroundColor: theme.background.modal,
                borderColor: theme.border.elements.default.color,
              },
            ]}
          >
            {qrCode}
            {centerIcon ? (
              <Box
                alignItems="center"
                justifyContent="center"
                style={centerOverlay}
              >
                {centerIcon}
              </Box>
            ) : null}
          </Box>

          {address ? (
            <Box alignItems="center" style={addressGroup}>
              <Text
                variant="body"
                style={[
                  addressText,
                  { color: theme.content.elements.secondary },
                ]}
              >
                {address}
              </Text>
              {onCopyAddress ? (
                <Pressable
                  onPress={handleCopy}
                  accessibilityRole="button"
                  accessibilityLabel={
                    copied ? t('common.addressCopied') : t('common.copyAddress')
                  }
                >
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    borderWidth={1}
                    borderRadius="element"
                    style={[
                      copyButton,
                      {
                        backgroundColor:
                          theme.background.button.secondary.default,
                        borderColor: theme.background.button.secondary.default,
                      },
                    ]}
                  >
                    <CopyAddressIcon />
                    <Text
                      variant="bodyMedium"
                      style={{ color: theme.content.button.secondary.default }}
                    >
                      {copied ? t('common.copied') : t('common.copyAddress')}
                    </Text>
                  </Box>
                </Pressable>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Box style={buttonContainer}>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={doneLabel}
          >
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              borderRadius="button"
              style={[
                doneButton,
                { backgroundColor: theme.background.button.primary.default },
              ]}
            >
              <Text
                variant="bodyMedium"
                style={{ color: theme.content.button.primary.default }}
              >
                {doneLabel}
              </Text>
            </Box>
          </Pressable>
        </Box>
      </Box>
    </Box>
  )
}

const sheet: ViewStyle = { paddingBottom: 34 }
const topBar: ViewStyle = {
  height: 64,
  paddingTop: 25,
  paddingBottom: 12,
  paddingHorizontal: 24,
}
// QR card and address group share a 40px gap (Figma `content`).
const content: ViewStyle = {
  paddingHorizontal: 20,
  gap: 40,
}
const qrFrame: ViewStyle = {
  width: 275,
  height: 275,
  borderRadius: 20,
  padding: 18,
  // Figma shadow/md: a soft lift under the card.
  shadowColor: '#000',
  shadowOpacity: 0.1,
  shadowOffset: { width: 0, height: 4 },
  shadowRadius: 16,
  elevation: 3,
}
const centerOverlay: ViewStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
}
const addressGroup: ViewStyle = { gap: 16 }
// Constrained so the 42-char address wraps to two centered lines (Figma).
const addressText: TextStyle = { textAlign: 'center', maxWidth: 220 }
const copyButton: ViewStyle = {
  height: 48,
  paddingHorizontal: 20,
  gap: 8,
}
const buttonContainer: ViewStyle = { paddingHorizontal: 20 }
const doneButton: ViewStyle = { height: 56, paddingHorizontal: 24 }
